/**
 * bundleHotspotService.js
 *
 * Uses the Matterport Bundle SDK (sdk.Scene) to place custom 3D sphere hotspots.
 * Component uses the function-constructor pattern required by the Bundle SDK.
 * Objects are added directly to context.scene (THREE.Scene) for reliability.
 */

const COMPONENT_NAME = 'mp.customHotspot';

// ─── Hotspot Component Factory ───────────────────────────────────────────────
// Must be a plain function constructor — NOT an ES6 class.
// SDK injects `context` and `outputs` before calling onInit.

function HotspotComponent() {
  this.inputs = {
    label: 'Hotspot',
    color: 0xff3300,
    radius: 0.4,
    // World-space position passed in so onInit can position the mesh directly
    worldX: 0,
    worldY: 0,
    worldZ: 0,
  };

  this._mesh = null;
  this._mat = null;
  this._startTime = 0;
}

HotspotComponent.prototype.onInit = function () {
  console.log('🟢 onInit — inputs:', JSON.stringify(this.inputs));
  try {
    const THREE = this.context.three;
    this._THREE = THREE; // stored for live radius updates
    const { color, radius, worldX, worldY, worldZ } = this.inputs;
    this._startTime = Date.now();

    // Large glowing sphere — visible from any direction nearby
    const geo = new THREE.SphereGeometry(radius, 16, 16);
    this._mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95 });
    this._mesh = new THREE.Mesh(geo, this._mat);

    // Set world position explicitly on the mesh itself
    this._mesh.position.set(worldX, worldY, worldZ);

    // Add directly to the THREE.js scene with explicit position
    // This bypasses all SDK node transform issues
    this.context.scene.add(this._mesh);

    // Also wire up outputs so SDK knows about this object
    this.outputs.objectRoot = this._mesh;
    this.outputs.collider  = this._mesh;

    console.log('🟢 Hotspot mesh added directly to scene at world pos:', worldX.toFixed(2), worldY.toFixed(2), worldZ.toFixed(2));
  } catch (err) {
    console.error('🔴 onInit FAILED:', err?.message, err);
  }
};

HotspotComponent.prototype.onTick = function () {
  if (!this._mesh) return;
  const t = (Date.now() - this._startTime) / 1000;
  const s = 1 + 0.12 * Math.sin(t * 2.5);
  this._mesh.scale.set(s, s, s);
};

HotspotComponent.prototype.onDestroy = function () {
  if (this._mesh) {
    // Remove from scene since we added it directly
    if (this.context?.scene) this.context.scene.remove(this._mesh);
    this._mesh.geometry.dispose();
  }
  if (this._mat) this._mat.dispose();
};

// ─── Service ────────────────────────────────────────────────────────────────

class BundleHotspotService {
  constructor() {
    this._registered = false;
    this._sceneObjects = []; // { sceneObject, label, instance: HotspotComponent }
    this._pendingInstance = null; // captured during SDK factory call
  }

  /**
   * Register the custom hotspot component factory with the Bundle SDK.
   * Must be called once after sdk.Scene is available (i.e. after connect).
   *
   * @param {object} sdk - The connected mpSdk instance from Bundle SDK
   */
  async registerHotspotComponent(sdk) {
    if (this._registered) return;

    if (!sdk.Scene) {
      throw new Error('sdk.Scene is not available — make sure you are using the Bundle SDK, not the Embed SDK');
    }

    const self = this;
    await sdk.Scene.register(COMPONENT_NAME, function () {
      const inst = new HotspotComponent();
      self._pendingInstance = inst; // capture before onInit fires
      return inst;
    });
    this._registered = true;
    console.log(`✅ Hotspot component "${COMPONENT_NAME}" registered`);
  }

  /**
   * Place a glowing hotspot sphere at a given 3D world position.
   *
   * @param {object} sdk - The connected mpSdk instance
   * @param {object} options
   * @param {{ x: number, y: number, z: number }} options.position - World space position
   * @param {string} [options.label='Hotspot'] - Display/event label
   * @param {number} [options.color=0x00aaff] - RGB hex color (e.g. 0xff0000 for red)
   * @param {number} [options.radius=0.15] - Sphere radius in meters
   * @returns {Promise<object>} The created sceneObject
   */
  async placeHotspot(sdk, { position, label = 'Hotspot', color = 0xff3300, radius = 0.4 } = {}) {
    console.log('🔧 placeHotspot — pos:', position);
    try {
      if (!this._registered) {
        await this.registerHotspotComponent(sdk);
      }

      const [sceneObject] = await sdk.Scene.createObjects(1);
      console.log('🔧 sceneObject obtained');

      const node = sceneObject.addNode();

      // Pass world position as inputs so onInit can set mesh.position directly
      node.addComponent(COMPONENT_NAME, {
        label, color, radius,
        worldX: position.x,
        worldY: position.y,
        worldZ: position.z,
      });
      console.log('🔧 addComponent done — calling start()');

      sceneObject.start(); // triggers onInit (and the factory captures the instance)
      console.log('🔧 start() done — onInit should have fired above');

      const instance = this._pendingInstance;
      this._pendingInstance = null;
      this._sceneObjects.push({ sceneObject, label, instance });
      console.log(`✅ Hotspot "${label}" placed at`, position);
      return sceneObject;
    } catch (err) {
      console.error('🔴 placeHotspot FAILED:', err?.message ?? err, err);
      throw err;
    }
  }

  /**
   * Live-update a hotspot's visual properties without re-creating it.
   * Directly mutates the THREE.js mesh/material — changes are immediate.
   *
   * @param {string} label   — must match the label passed to placeHotspot()
   * @param {object} updates — subset of { position, markerColor, markerRadius }
   */
  updateHotspot(label, updates) {
    const entry = this._sceneObjects.find(e => e.label === label);
    if (!entry?.instance?._mesh) return;

    const { _mesh: mesh, _mat: mat, _THREE: THREE } = entry.instance;

    if (updates.position) {
      const { x, y, z } = updates.position;
      mesh.position.set(
        x !== undefined ? x : mesh.position.x,
        y !== undefined ? y : mesh.position.y,
        z !== undefined ? z : mesh.position.z,
      );
    }

    if (updates.markerColor !== undefined && mat) {
      const colorNum = parseInt(String(updates.markerColor).replace('0x', ''), 16);
      mat.color.setHex(colorNum);
    }

    if (updates.markerRadius !== undefined && THREE && mesh) {
      mesh.geometry.dispose();
      mesh.geometry = new THREE.SphereGeometry(updates.markerRadius, 16, 16);
    }
  }

  /**
   * Remove all hotspots placed by this service.
   */
  removeAll() {
    this._sceneObjects.forEach(({ sceneObject }) => {
      try { sceneObject.stop(); } catch (_) {}
    });
    this._sceneObjects = [];
    console.log('🗑️ All hotspots removed');
  }
}

const bundleHotspotService = new BundleHotspotService();
export default bundleHotspotService;
